import * as XLSX from "xlsx";
import { format } from "date-fns";

export type ScoreDataAllCities = {
  city: string;
  score: number;
};

export type ScoreDataByDate = {
  date: string;
  score: number;
};

interface ExportScoreOperationParams {
  data: ScoreDataAllCities[] | ScoreDataByDate[];
  isAllCities: boolean;
  cityName?: string;
  getSiglaFn?: (city: string) => string;
}

/**
 * Exporta dados de score de operação para Excel
 * @param data - Array com dados de score (por cidade ou por data)
 * @param isAllCities - Se true, exporta todas as cidades. Se false, exporta por dias
 * @param cityName - Nome da cidade (usado no nome do arquivo quando não é todas)
 * @param getSiglaFn - Função para converter nome da cidade em sigla
 */
export const exportScoreOperationToExcel = ({
  data,
  isAllCities,
  cityName = "Cidade",
  getSiglaFn,
}: ExportScoreOperationParams) => {
  if (!data || data.length === 0) {
    alert("Não há dados para exportar");
    return;
  }

  let dataToExport: Record<string, string | number>[] = [];

  if (isAllCities) {
    // Exportar todas as cidades com suas notas
    dataToExport = (data as ScoreDataAllCities[]).map(({ city, score }) => ({
      Cidade: getSiglaFn ? getSiglaFn(city) : city,
      Nota: Number(score.toFixed(2)),
    }));
  } else {
    // Exportar dias e notas da cidade específica
    dataToExport = (data as ScoreDataByDate[]).map(({ date, score }) => ({
      Dia: date,
      Nota: Number(score.toFixed(2)),
    }));
  }

  // Criar workbook e worksheet
  const worksheet = XLSX.utils.json_to_sheet(dataToExport);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Notas de Operação");

  // Ajustar largura das colunas
  const maxWidths = Object.keys(dataToExport[0] || {}).map((key) => {
    const values = dataToExport.map((row) => String(row[key] || ""));
    const maxLength = Math.max(
      key.length,
      ...values.map((val) => val.length)
    );
    return { wch: Math.min(maxLength + 2, 30) }; // Max 30 caracteres
  });
  worksheet["!cols"] = maxWidths;

  // Aplicar estilo ao cabeçalho (opcional, se quiser adicionar cores)
  const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const address = XLSX.utils.encode_col(C) + "1";
    if (!worksheet[address]) continue;
    worksheet[address].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: "4472C4" } },
    };
  }

  // Nome do arquivo
  const today = format(new Date(), "dd-MM-yyyy");
  const fileName = isAllCities
    ? `Notas_Operacao_Brasil_${today}.xlsx`
    : `Notas_Operacao_${cityName}_${today}.xlsx`;

  // Download do arquivo
  XLSX.writeFile(workbook, fileName);
};

// Função helper para exportar apenas cidades
export const exportAllCitiesScore = (
  scores: ScoreDataAllCities[],
  getSiglaFn?: (city: string) => string
) => {
  exportScoreOperationToExcel({
    data: scores,
    isAllCities: true,
    getSiglaFn,
  });
};

// Função helper para exportar dias de uma cidade
export const exportCityScoreByDate = (
  scores: ScoreDataByDate[],
  cityName: string
) => {
  exportScoreOperationToExcel({
    data: scores,
    isAllCities: false,
    cityName,
  });
};